import { canonicalWorkAreaKey } from "./work-area-keys";

/** Project therapist declarations for ranking and display. */
export type PracticeProjectionSource = {
  practiceUpdatedAt?: Date | string | null;
  concerns?: readonly {
    concernKey: string;
    tier: string;
    concern?: {
      label: string;
      aliases?: readonly string[];
      examples?: readonly string[];
    } | null;
  }[];
  approaches?: readonly { key: string; catalog?: { label: string } }[];
};

export function projectPractice(source: PracticeProjectionSource) {
  const primary = new Set<string>();
  const additional = new Set<string>();
  const labels: Record<string, string> = {};
  for (const entry of source.concerns ?? []) {
    const key = canonicalWorkAreaKey(entry.concernKey);
    (entry.tier === "PRIMARY" ? primary : additional).add(key);
    if (entry.concern) labels[key] = entry.concern.label;
  }
  return {
    primary: [...primary],
    additional: [...additional].filter((key) => !primary.has(key)),
    labels,
    modalityPrimary: source.approaches?.[0]?.key ?? null,
    modalityLabel: source.approaches?.[0]?.catalog?.label,
  };
}

export function practiceSpecialtyLabels(
  source: PracticeProjectionSource & { specialties: string[] },
) {
  if (!source.practiceUpdatedAt && !source.concerns?.length)
    return source.specialties;
  const practice = projectPractice(source);
  return [...practice.primary, ...practice.additional].flatMap((key) =>
    practice.labels[key] ? [practice.labels[key]] : [],
  );
}

export function practiceCardFields(source: PracticeProjectionSource) {
  const practice = projectPractice(source);
  return {
    focusAreasTop: [...practice.primary, ...practice.additional],
    focusAreaLabels: practice.labels,
    modalityPrimary: practice.modalityPrimary,
    modalityLabel: practice.modalityLabel,
  };
}

/** Internal migration/summary provenance and ranking counters are not public profile facts. */
export function omitInternalPracticeFields<
  T extends Partial<
    Record<
      | "practiceRevision"
      | "practiceOrigin"
      | "practiceSourceHash"
      | "practiceMethodVersion"
      | "aiSummarySourceHash"
      | "recommendationExposureScore"
      | "recommendationExposureUpdatedAt",
      unknown
    >
  >,
>(source: T) {
  const {
    practiceRevision: _revision,
    practiceOrigin: _origin,
    practiceSourceHash: _sourceHash,
    practiceMethodVersion: _methodVersion,
    aiSummarySourceHash: _summaryHash,
    recommendationExposureScore: _exposureScore,
    recommendationExposureUpdatedAt: _exposureUpdatedAt,
    ...publicFields
  } = source;
  return publicFields;
}
